import React, { useEffect, useState, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useT } from './i18n/index';
import type { Translations } from './i18n/types';

interface TourStep {
  target: string;       // data-tour attribute value, or 'canvas' for center
  titleKey: keyof Translations;
  descKey?: keyof Translations;
  description?: string | React.ReactNode;
  position?: 'bottom' | 'top' | 'left' | 'right' | 'center';
}

interface TourOverlayProps {
  onClose: () => void;
}

const STEPS: TourStep[] = [
  { target: 'title', titleKey: 'tourTitleName', descKey: 'tourDescName', position: 'bottom' },
  { target: 'btn-new', titleKey: 'tourTitleNew', descKey: 'tourDescNew', position: 'bottom' },
  { target: 'btn-open', titleKey: 'tourTitleOpen', descKey: 'tourDescOpen', position: 'bottom' },
  { target: 'btn-save', titleKey: 'tourTitleSave', descKey: 'tourDescSave', position: 'bottom' },
  { target: 'btn-share', titleKey: 'tourTitleShare', descKey: 'tourDescShare', position: 'bottom' },
  { target: 'toggle-autosave', titleKey: 'tourTitleAutosave', descKey: 'tourDescAutosave', position: 'bottom' },
  { target: 'mode-switch', titleKey: 'tourTitleMode', descKey: 'tourDescMode', position: 'bottom' },
  { target: 'toggle-connect', titleKey: 'tourTitleConnect', descKey: 'tourDescConnect', position: 'bottom' },
  { target: 'btn-gantt', titleKey: 'tourTitleGantt', descKey: 'tourDescGantt', position: 'bottom' },
  { target: 'zoom-controls', titleKey: 'tourTitleZoom', descKey: 'tourDescZoom', position: 'bottom' },
  { target: 'btn-box', titleKey: 'tourTitleAddBox', descKey: 'tourDescAddBox', position: 'right' },
  { target: 'btn-note', titleKey: 'tourTitleAddNote', descKey: 'tourDescAddNote', position: 'right' },
  { target: 'btn-start', titleKey: 'tourTitleStartDay', descKey: 'tourDescStartDay', position: 'right' },
  { target: 'btn-category', titleKey: 'tourTitleCategory', descKey: 'tourDescCategory', position: 'right' },
  { target: 'btn-resources', titleKey: 'tourTitleResources', descKey: 'tourDescResources', position: 'right' },
  { target: 'canvas', titleKey: 'tourTitleCanvas', position: 'center' },
];

interface SpotlightRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

const PADDING = 6;

export default function TourOverlay({ onClose }: TourOverlayProps) {
  const { t } = useT();
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState<SpotlightRect | null>(null);

  const currentStep = STEPS[step];

  const canvasControls = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {[
        { icon: '🖱️', keys: 'Scroll wheel', action: t('ctrlZoom') },
        { icon: '🖱️', keys: 'Right-click + drag', action: t('ctrlPan') },
        { icon: '🖱️', keys: 'Left-click + drag', action: t('ctrlSelectMultiple') },
        { icon: '⌨️', keys: 'Ctrl + C / V', action: t('ctrlCopyPaste') },
        { icon: '⌨️', keys: 'Ctrl + Z / Y', action: t('ctrlUndoRedo') },
      ].map(({ icon, keys, action }) => (
        <div key={keys} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 16, width: 22, textAlign: 'center', flexShrink: 0 }}>{icon}</span>
          <span style={{
            background: '#f3f4f6',
            border: '1px solid #e5e7eb',
            borderRadius: 5,
            padding: '2px 7px',
            fontSize: 11,
            fontWeight: 600,
            color: '#374151',
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}>{keys}</span>
          <span style={{ fontSize: 12, color: '#6b7280' }}>{action}</span>
        </div>
      ))}
    </div>
  );

  const updateRect = useCallback(() => {
    if (currentStep.position === 'center') { setRect(null); return; }
    const el = document.querySelector(`[data-tour="${currentStep.target}"]`);
    if (!el) { setRect(null); return; }
    const r = el.getBoundingClientRect();
    setRect({
      top: r.top - PADDING,
      left: r.left - PADDING,
      width: r.width + PADDING * 2,
      height: r.height + PADDING * 2,
    });
  }, [currentStep.target, currentStep.position]);

  useEffect(() => {
    updateRect();
    window.addEventListener('resize', updateRect);
    return () => window.removeEventListener('resize', updateRect);
  }, [updateRect]);

  const prev = () => setStep(s => Math.max(0, s - 1));
  const next = () => {
    if (step < STEPS.length - 1) setStep(s => s + 1);
    else onClose();
  };

  // Tooltip positioning relative to the spotlight rect
  const getTooltipStyle = (): React.CSSProperties => {
    const pos = currentStep.position ?? 'bottom';
    if (pos === 'center' || !rect) {
      return {
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 400,
      };
    }
    const tooltipW = 340;
    const tooltipOffset = 16;

    switch (pos) {
      case 'bottom':
        return {
          top: rect.top + rect.height + tooltipOffset,
          left: Math.min(
            Math.max(rect.left + rect.width / 2 - tooltipW / 2, 8),
            window.innerWidth - tooltipW - 8
          ),
          width: tooltipW,
        };
      case 'top':
        return {
          top: rect.top - tooltipOffset - 120,
          left: Math.min(
            Math.max(rect.left + rect.width / 2 - tooltipW / 2, 8),
            window.innerWidth - tooltipW - 8
          ),
          width: tooltipW,
        };
      case 'left':
        return {
          top: rect.top + rect.height / 2 - 60,
          left: Math.max(rect.left - tooltipW - tooltipOffset, 8),
          width: tooltipW,
        };
      case 'right':
        return {
          top: rect.top + rect.height / 2 - 60,
          left: rect.left + rect.width + tooltipOffset,
          width: tooltipW,
        };
    }
  };

  // Arrow indicator direction
  const getArrowClass = () => {
    const pos = currentStep.position ?? 'bottom';
    if (pos === 'center') return '';
    return pos === 'bottom' ? 'tour-arrow-up' :
           pos === 'top'    ? 'tour-arrow-down' :
           pos === 'left'   ? 'tour-arrow-right' : 'tour-arrow-left';
  };

  return (
    <>
      {/* Dark overlay with spotlight cutout using box-shadow */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 9998,
          pointerEvents: 'none',
        }}
      >
        {/* Four dimmed panels around the spotlight */}
        {rect && (
          <>
            {/* Top */}
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: rect.top, background: 'rgba(0,0,0,0.55)' }} />
            {/* Bottom */}
            <div style={{ position: 'absolute', top: rect.top + rect.height, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.55)' }} />
            {/* Left */}
            <div style={{ position: 'absolute', top: rect.top, left: 0, width: rect.left, height: rect.height, background: 'rgba(0,0,0,0.55)' }} />
            {/* Right */}
            <div style={{ position: 'absolute', top: rect.top, left: rect.left + rect.width, right: 0, height: rect.height, background: 'rgba(0,0,0,0.55)' }} />
            {/* Spotlight border ring */}
            <div style={{
              position: 'absolute',
              top: rect.top,
              left: rect.left,
              width: rect.width,
              height: rect.height,
              borderRadius: 6,
              boxShadow: '0 0 0 2px #3b82f6, 0 0 0 4px rgba(59,130,246,0.3)',
              transition: 'all 0.25s ease',
            }} />
          </>
        )}
        {/* Full dim when no element found */}
        {!rect && <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.55)' }} />}
      </div>

      {/* Clickable close layer (outside tooltip) */}
      <div
        style={{ position: 'fixed', inset: 0, zIndex: 9999, cursor: 'default' }}
        onClick={onClose}
      />

      {/* Tooltip bubble */}
      <div
        style={{
          position: 'fixed',
          zIndex: 10000,
          ...getTooltipStyle(),
          transition: 'top 0.25s ease, left 0.25s ease',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Arrow indicator */}
        <style>{`
          .tour-arrow-up::before {
            content: '';
            position: absolute;
            top: -7px;
            left: 24px;
            border: 7px solid transparent;
            border-bottom-color: white;
            border-top: none;
          }
          .tour-arrow-down::before {
            content: '';
            position: absolute;
            bottom: -7px;
            left: 24px;
            border: 7px solid transparent;
            border-top-color: white;
            border-bottom: none;
          }
          .tour-arrow-right::before {
            content: '';
            position: absolute;
            right: -7px;
            top: 20px;
            border: 7px solid transparent;
            border-left-color: white;
            border-right: none;
          }
          .tour-arrow-left::before {
            content: '';
            position: absolute;
            left: -7px;
            top: 20px;
            border: 7px solid transparent;
            border-right-color: white;
            border-left: none;
          }
        `}</style>

        <div
          className={getArrowClass()}
          style={{
            position: 'relative',
            background: 'white',
            borderRadius: 10,
            boxShadow: '0 8px 32px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.1)',
            padding: '16px',
          }}
        >
          {/* Close button */}
          <button
            onClick={onClose}
            style={{
              position: 'absolute',
              top: 10,
              right: 10,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#9ca3af',
              padding: 2,
              borderRadius: 4,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <X size={14} />
          </button>

          {/* Step counter */}
          <div style={{ fontSize: 11, color: '#6366f1', fontWeight: 600, marginBottom: 4, letterSpacing: '0.05em' }}>
            {t('tourStepOf', { current: step + 1, total: STEPS.length })}
          </div>

          {/* Title */}
          <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', marginBottom: 6, paddingRight: 20 }}>
            {t(currentStep.titleKey)}
          </div>

          {/* Description */}
          <div style={{ fontSize: 13, color: '#4b5563', lineHeight: 1.55, marginBottom: 14 }}>
            {currentStep.descKey ? t(currentStep.descKey) : canvasControls}
          </div>

          {/* Progress dots */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', gap: 4 }}>
              {STEPS.map((_, i) => (
                <div
                  key={i}
                  onClick={() => setStep(i)}
                  style={{
                    width: i === step ? 16 : 6,
                    height: 6,
                    borderRadius: 3,
                    background: i === step ? '#6366f1' : '#e5e7eb',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                />
              ))}
            </div>

            {/* Prev / Next */}
            <div style={{ display: 'flex', gap: 6 }}>
              {step > 0 && (
                <button
                  onClick={prev}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    padding: '5px 10px',
                    borderRadius: 6,
                    border: '1px solid #e5e7eb',
                    background: 'white',
                    fontSize: 12,
                    fontWeight: 600,
                    color: '#374151',
                    cursor: 'pointer',
                  }}
                >
                  <ChevronLeft size={13} /> {t('back')}
                </button>
              )}
              <button
                onClick={next}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  padding: '5px 12px',
                  borderRadius: 6,
                  border: 'none',
                  background: 'linear-gradient(135deg, #6366f1, #3b82f6)',
                  fontSize: 12,
                  fontWeight: 600,
                  color: 'white',
                  cursor: 'pointer',
                }}
              >
                {step < STEPS.length - 1 ? <>{t('next')} <ChevronRight size={13} /></> : t('done')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
